import { type KeyboardEvent, type PointerEvent, useState } from 'react';

export interface DailyBalance {
  day: string;
  remaining: number;
}

const W = 120;
const H = 32;
const PAD = 3;

const formatDay = (day: string) => day.slice(5).split('-').reverse().join('/');

/**
 * 30-day balance trend for one provider. The line is muted and the last day
 * is marked; pointer or arrow keys snap to a day and read its value out below.
 * It is a slider over the days, so a screen reader hears each day's value.
 */
export const CreditSparkline = ({ series, unit }: { series: DailyBalance[]; unit: string }) => {
  const [active, setActive] = useState<number | null>(null);
  if (series.length < 2) return null;

  const values = series.map((p) => p.remaining);
  const min = Math.min(...values);
  const span = Math.max(...values) - min || 1;
  const x = (i: number) => (i / (series.length - 1)) * W;
  const y = (v: number) => H - PAD - ((v - min) / span) * (H - 2 * PAD);
  const points = series.map((p, i) => `${x(i).toFixed(1)},${y(p.remaining).toFixed(1)}`);
  const lastIndex = series.length - 1;
  const shown = active ?? lastIndex;
  const point = series[shown];

  const snap = (e: PointerEvent<HTMLDivElement>) => {
    const box = e.currentTarget.getBoundingClientRect();
    const ratio = Math.min(1, Math.max(0, (e.clientX - box.left) / box.width));
    setActive(Math.round(ratio * lastIndex));
  };
  const step = (e: KeyboardEvent<HTMLDivElement>) => {
    const delta = e.key === 'ArrowLeft' ? -1 : e.key === 'ArrowRight' ? 1 : 0;
    if (!delta) return;
    e.preventDefault();
    setActive(Math.min(lastIndex, Math.max(0, shown + delta)));
  };

  const readout = point
    ? `${formatDay(point.day)}: ${point.remaining.toLocaleString('pt-BR')} ${unit}`
    : '';

  return (
    <div className="flex shrink-0 flex-col items-end gap-0.5">
      <div
        className="h-8 w-28 cursor-crosshair rounded focus-visible:outline-2"
        role="slider"
        aria-label={`Saldo por dia nos últimos ${series.length} dias`}
        aria-valuemin={0}
        aria-valuemax={lastIndex}
        aria-valuenow={shown}
        aria-valuetext={readout}
        tabIndex={0}
        onPointerMove={snap}
        onPointerLeave={() => setActive(null)}
        onBlur={() => setActive(null)}
        onKeyDown={step}
      >
        <svg viewBox={`0 0 ${W} ${H}`} className="h-full w-full overflow-visible" aria-hidden>
          <polyline
            points={points.join(' ')}
            fill="none"
            stroke="var(--color-muted-fg)"
            strokeWidth={2}
            strokeLinejoin="round"
            strokeLinecap="round"
            vectorEffect="non-scaling-stroke"
          />
          {active !== null && (
            <line
              x1={x(shown)}
              x2={x(shown)}
              y1={0}
              y2={H}
              stroke="var(--color-border)"
              strokeWidth={1}
              vectorEffect="non-scaling-stroke"
            />
          )}
          {point && (
            <circle
              cx={x(shown)}
              cy={y(point.remaining)}
              r={3}
              fill="var(--color-fg)"
              stroke="var(--color-surface)"
              strokeWidth={2}
              vectorEffect="non-scaling-stroke"
            />
          )}
        </svg>
      </div>
      <span className="text-[11px] tabular-nums text-muted-fg" aria-hidden>
        {active !== null ? readout : `${series.length} dias`}
      </span>
    </div>
  );
};
